import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Search, Trash2, Phone, Mail, RefreshCw, ClipboardPlus, Link2 } from "lucide-react";
import { api, errorText, SERVICES, STATUSES } from "@/lib/api";

const svcLabel = (v) => SERVICES.find((s) => s.value === v)?.label ?? v;
const STATUS_COLOR = { ny: "bg-[var(--red)]", kontaktet: "bg-amber-500", booket: "bg-sky-500", fullfort: "bg-emerald-500", avvist: "bg-zinc-500" };

const Row = ({ i, onStatus, onDelete, onConvert }) => (
  <article data-testid={`inquiry-row-${i._id}`} className="grid gap-4 border border-white/10 bg-[var(--surface)] p-5 md:grid-cols-12 md:items-start">
    <div className="md:col-span-4">
      <div className="flex items-center gap-2">
        <span className={`h-2 w-2 rounded-full ${STATUS_COLOR[i.status] ?? "bg-zinc-500"}`} />
        <h3 className="font-display text-2xl font-bold uppercase leading-none">{i.name}</h3>
      </div>
      <div className="mt-2 flex flex-col gap-1 text-sm text-zinc-300">
        <a href={`tel:${i.phone}`} className="flex items-center gap-2 hover:text-white"><Phone size={13} />{i.phone}</a>
        {i.email && <a href={`mailto:${i.email}`} className="flex items-center gap-2 hover:text-white"><Mail size={13} />{i.email}</a>}
      </div>
    </div>
    <div className="md:col-span-5">
      <div className="font-mono text-xs uppercase tracking-wider text-[var(--red)]">{svcLabel(i.service)}</div>
      <div className="mt-1 text-base text-white">{[i.car, i.engine, i.year, i.regnr].filter(Boolean).join(" · ")}</div>
      {i.stage1 && <div className="mt-1 font-mono text-xs text-zinc-300">Stage 1: {i.stage1}</div>}
      {i.message && <p className="mt-2 whitespace-pre-line text-sm text-zinc-400">{i.message}</p>}
      <div className="mt-2 font-mono text-[11px] text-zinc-500">{new Date(i.created_at).toLocaleString("nb-NO")} · e-postvarsel: {{ sent: "sendt", skipped: "ikke sendt", failed: "feilet", pending: "venter" }[i.email_status] ?? i.email_status}</div>
    </div>
    <div className="flex flex-wrap items-center gap-2 md:col-span-3 md:justify-end">
      <button onClick={() => onConvert(i)} data-testid={`inquiry-convert-${i._id}`}
        className="flex items-center gap-1.5 border border-white/15 px-3 py-2 font-mono text-[11px] uppercase text-white hover:border-white/40">
        {i.work_order_id ? <><Link2 size={13} /> Åpne arbeidsordre</> : <><ClipboardPlus size={13} /> Kunde + arbeidsordre</>}
      </button>
      <select value={i.status} onChange={(e) => onStatus(i._id, e.target.value)} data-testid={`inquiry-status-${i._id}`}
        className="border border-white/15 bg-[#08090b] px-3 py-2 font-mono text-xs uppercase text-white outline-none focus:border-[var(--red)]">
        {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
      </select>
      <button onClick={() => onDelete(i._id)} data-testid={`inquiry-delete-${i._id}`} aria-label="Slett"
        className="border border-white/15 p-2 text-zinc-400 hover:border-red-500 hover:text-red-400"><Trash2 size={16} /></button>
    </div>
  </article>
);

export const InquiryList = ({ onOpenOrder }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [svc, setSvc] = useState("alle");
  const [status, setStatus] = useState("alle");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/inquiries");
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const onStatus = async (id, s) => {
    try {
      const { data } = await api.patch(`/inquiries/${id}`, { status: s });
      setItems((xs) => xs.map((x) => (x._id === id ? data : x)));
      toast.success("Status oppdatert");
    } catch (e) { toast.error(errorText(e)); }
  };
  const onConvert = async (i) => {
    try {
      const { data } = await api.post(`/admin/inquiries/${i._id}/convert`);
      setItems((xs) => xs.map((x) => (x._id === i._id ? { ...x, customer_id: data.customer_id, work_order_id: data.work_order_id } : x)));
      if (!data.existing) toast.success("Kunde og arbeidsordre opprettet");
      onOpenOrder?.(data.work_order_id);
    } catch (e) { toast.error(errorText(e)); }
  };
  const onDelete = async (id) => {
    if (!window.confirm("Slette denne forespørselen?")) return;
    try {
      await api.delete(`/inquiries/${id}`);
      setItems((xs) => xs.filter((x) => x._id !== id));
      toast.success("Slettet");
    } catch (e) { toast.error(errorText(e)); }
  };

  const shown = useMemo(() => items.filter((i) =>
    (svc === "alle" || i.service === svc) && (status === "alle" || i.status === status) &&
    `${i.name} ${i.phone} ${i.email ?? ""} ${i.car} ${i.engine ?? ""} ${i.regnr ?? ""}`.toLowerCase().includes(q.toLowerCase())), [items, q, svc, status]);
  const newCount = items.filter((i) => i.status === "ny").length;
  const sel = "border border-white/15 bg-[#08090b] px-3 py-2.5 font-mono text-xs uppercase text-white outline-none focus:border-[var(--red)]";

  return (
    <main className="mx-auto max-w-7xl px-5 py-10 sm:px-8">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-5xl font-black uppercase leading-none">Forespørsler</h1>
          <p className="mt-2 font-mono text-xs uppercase tracking-widest text-zinc-400" data-testid="admin-counts">{items.length} totalt · {newCount} nye</p>
        </div>
        <button onClick={load} data-testid="admin-refresh-button" className="flex items-center gap-2 border border-white/15 px-3 py-2 font-mono text-xs uppercase hover:border-white/40">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Oppdater
        </button>
      </div>
      <div className="mb-6 flex flex-col gap-3 md:flex-row">
        <div className="flex flex-1 items-center gap-2 border border-white/15 px-3">
          <Search size={16} className="text-zinc-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Søk navn, telefon, bil..." data-testid="admin-search-input"
            className="w-full bg-transparent py-2.5 text-sm text-white outline-none placeholder:text-zinc-500" />
        </div>
        <select value={svc} onChange={(e) => setSvc(e.target.value)} className={sel} data-testid="admin-filter-service">
          <option value="alle">Alle tjenester</option>
          {SERVICES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className={sel} data-testid="admin-filter-status">
          <option value="alle">Alle statuser</option>
          {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>
      <div className="space-y-3" data-testid="admin-inquiry-list">
        {!loading && shown.length === 0 && <p className="border border-dashed border-white/15 p-10 text-center text-zinc-400" data-testid="admin-empty">Ingen forespørsler.</p>}
        {shown.map((i) => <Row key={i._id} i={i} onStatus={onStatus} onDelete={onDelete} onConvert={onConvert} />)}
      </div>
    </main>
  );
};
